// Work-volume scene primitives: the cube's six surfaces (4 walls + ceiling
// + floor) and the 12-edge cage that traces its outline. Pure data + types
// + helpers; the live state (mutable bounds, the actual meshes, the
// per-frame visibility update) stays in main.ts where it's threaded
// through input handling and the render loop.

import * as THREE from "three";

// Current work-volume extents in gridGroup-local meters. Walls are
// independent (asymmetric) — the footprint need not be centered on the
// origin. Floor is fixed at y=0; ceiling at y=height.
export interface VolumeBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  height: number;
}

// One of the six cube surfaces (4 walls + ceiling + floor). `axis`
// (0=x, 1=y, 2=z) is the surface's normal axis; `coord` is the constant
// value along that axis in gridGroup-local meters; `outwardSign` is +1 if
// the outward direction is +axis, -1 otherwise.
export interface WorkVolumeSurface {
  mesh: THREE.Mesh;
  axis: 0 | 1 | 2;
  outwardSign: 1 | -1;
  coord: number;
}

// Spec for the five resizable walls/ceiling. Floor isn't here because its
// mesh (600m × 600m ground) is fixed regardless of the volume size — only
// these five rebuild on resize.
export type SurfaceSpec = {
  axis: 0 | 1 | 2;
  outwardSign: 1 | -1;
  rot: [number, number, number];
  // Compute current position + plane size from current volume bounds.
  // pos[axis] is the surface's `coord`; the other two place it on the cube.
  layout: (b: VolumeBounds) => {
    pos: [number, number, number];
    width: number;
    height: number;
  };
};

// Footprint center + spans, derived from the asymmetric edges.
const cx = (b: VolumeBounds): number => (b.minX + b.maxX) / 2;
const cz = (b: VolumeBounds): number => (b.minZ + b.maxZ) / 2;
const spanX = (b: VolumeBounds): number => b.maxX - b.minX;
const spanZ = (b: VolumeBounds): number => b.maxZ - b.minZ;

export const SURFACE_SPECS: SurfaceSpec[] = [
  // North wall (z = minZ): plane is in XY by default — just translate.
  {
    axis: 2,
    outwardSign: -1,
    rot: [0, 0, 0],
    layout: (b) => ({
      pos: [cx(b), b.height / 2, b.minZ],
      width: spanX(b),
      height: b.height,
    }),
  },
  // South wall (z = maxZ): same default orientation; DoubleSide handles facing.
  {
    axis: 2,
    outwardSign: +1,
    rot: [0, 0, 0],
    layout: (b) => ({
      pos: [cx(b), b.height / 2, b.maxZ],
      width: spanX(b),
      height: b.height,
    }),
  },
  // East wall (x = maxX): rotate -π/2 around Y so the plane spans YZ.
  {
    axis: 0,
    outwardSign: +1,
    rot: [0, -Math.PI / 2, 0],
    layout: (b) => ({
      pos: [b.maxX, b.height / 2, cz(b)],
      width: spanZ(b),
      height: b.height,
    }),
  },
  // West wall (x = minX): symmetric.
  {
    axis: 0,
    outwardSign: -1,
    rot: [0, +Math.PI / 2, 0],
    layout: (b) => ({
      pos: [b.minX, b.height / 2, cz(b)],
      width: spanZ(b),
      height: b.height,
    }),
  },
  // Ceiling (y = height): rotate -π/2 around X so the plane spans XZ.
  {
    axis: 1,
    outwardSign: +1,
    rot: [-Math.PI / 2, 0, 0],
    layout: (b) => ({
      pos: [cx(b), b.height, cz(b)],
      width: spanX(b),
      height: spanZ(b),
    }),
  },
];

// Cube cage — 12 edges, each visible only when every adjacent surface
// is visible. Bottom edges only need the floor; top edges only need
// the ceiling — that way the perimeter outline of the floor/ceiling
// stays drawn even when the wall facing the camera is culled away so
// the user can see inside. Vertical edges need both of their adjacent
// walls so a vertical doesn't float alone when both walls are gone.
// Surface indices below match the push order in `workVolumeSurfaces`:
//   0 = north (z=-halfZ)  1 = south (z=+halfZ)  2 = east (x=+halfX)
//   3 = west  (x=-halfX)  4 = ceiling (y=height)  5 = floor (y=0)
//
// Each corner is encoded by signs (sx, sy, sz) ∈ {-1, +1}: gridGroup-local
// pos is (sx*halfX, sy>0 ? height : 0, sz*halfZ). Storing signs lets the
// cage rebuild against the current bounds on resize.
export type CornerSigns = [-1 | 1, -1 | 1, -1 | 1];
export interface CageEdgeDef {
  a: CornerSigns;
  b: CornerSigns;
  // Surface indices in workVolumeSurfaces — edge visible iff all are visible.
  surfaces: number[];
}
export const cageEdgeDefs: CageEdgeDef[] = [
  // Bottom loop — full perimeter of the floor.
  { a: [-1, -1, -1], b: [+1, -1, -1], surfaces: [5] }, // bottom-N
  { a: [+1, -1, -1], b: [+1, -1, +1], surfaces: [5] }, // bottom-E
  { a: [+1, -1, +1], b: [-1, -1, +1], surfaces: [5] }, // bottom-S
  { a: [-1, -1, +1], b: [-1, -1, -1], surfaces: [5] }, // bottom-W
  // Top loop — full perimeter of the ceiling.
  { a: [-1, +1, -1], b: [+1, +1, -1], surfaces: [4] }, // top-N
  { a: [+1, +1, -1], b: [+1, +1, +1], surfaces: [4] }, // top-E
  { a: [+1, +1, +1], b: [-1, +1, +1], surfaces: [4] }, // top-S
  { a: [-1, +1, +1], b: [-1, +1, -1], surfaces: [4] }, // top-W
  // Vertical edges — two walls each.
  { a: [-1, -1, -1], b: [-1, +1, -1], surfaces: [0, 3] }, // NW
  { a: [+1, -1, -1], b: [+1, +1, -1], surfaces: [0, 2] }, // NE
  { a: [+1, -1, +1], b: [+1, +1, +1], surfaces: [1, 2] }, // SE
  { a: [-1, -1, +1], b: [-1, +1, +1], surfaces: [1, 3] }, // SW
];

// Resolve a corner signs tuple to a gridGroup-local position given the
// current bounds. Used by the cage rebuild pass.
export function corner(
  s: CornerSigns,
  b: VolumeBounds,
): [number, number, number] {
  return [
    s[0] < 0 ? b.minX : b.maxX,
    s[1] > 0 ? b.height : 0,
    s[2] < 0 ? b.minZ : b.maxZ,
  ];
}
